package ai.wagemix.web.screens

import ai.wagemix.web.components.WMMixCardStrings
import ai.wagemix.web.components.WMOccupationRowStrings
import ai.wagemix.web.components.WMTreeRowStrings
import ai.wagemix.web.components.WMUploadDropzoneStrings
import ai.wagemix.web.components.WMUploadRowStrings
import ai.wagemix.web.components.WMFilePreviewStrings
import ai.wagemix.net.models.firebase.WMSubscriptionType
import com.fsryan.foundation.web.screens.FSBasePromptStrings

/**
 * Every user-visible string of the screens, grouped by screen. Compose resources are not
 * available to Kobweb, so the site passes an instance ([english] by default).
 */
data class WMWebStrings(
    val common: Common,
    val shell: Shell,
    val mixBuilder: MixBuilder,
    val picker: Picker,
    val savedMixes: SavedMixes,
    val jobDescriptions: JobDescriptions,
    val share: Share,
    val account: Account,
    val pricing: Pricing,
    val payment: Payment,
    val auth: Auth,
    val sharedMix: SharedMix,
    val sharedLinks: SharedLinks,
    val renewal: Renewal,
    val basePrompts: FSBasePromptStrings = FSBasePromptStrings.english
) {
    data class Common(
        val cancel: String,
        val close: String,
        val save: String,
        val delete: String,
        val retry: String,
        val dismiss: String,
        val signIn: String,
        val register: String,
        val openApp: String,
        val viewPlans: String,
        /** The upgrade for a Basic account: Pro is the only plan that adds anything (design deviations D4, E2). */
        val upgradeToPro: String,
        val loading: String,
        val annual: String,
        val hourly: String,
        val menu: String,
        val closeMenu: String,
        val dataSource: String,
        val projectedNote: String,
        val free: String,
        val basic: String,
        val pro: String
    )

    data class Shell(
        val mixBuilder: String,
        val savedMixes: String,
        val sharedLinks: String,
        val jobDescriptions: String,
        val methodology: String,
        val account: String,
        val newMix: String,
        val yourMixes: String,
        val logOut: String,
        val unsavedMix: String,
        val planFree: String,
        val planFreeText: String,
        /** The Basic plan card's one sentence with the count (design deviation A5): "2 of 3 job description uploads used". */
        val planBasicText: (used: Int, quota: Int) -> String,
        val planProText: String,
        val upgrade: String,
        val theme: String
    )

    data class MixBuilder(
        val title: String,
        /** The top bar's badge on a saved mix generated from a job description. */
        val aiMix: String,
        /** The banner above such a mix: what the AI did, and what the user can do with it. */
        val aiMixTitle: String,
        val aiMixMessage: String,
        /** The notice and the Re-evaluate button while the mix is being re-evaluated. */
        val reevaluatingTitle: String,
        val reevaluatingMessage: String,
        val reevaluating: String,
        val occupations: String,
        val addOccupation: String,
        val emptyTitle: String,
        val emptyMessage: String,
        val total: String,
        val filters: String,
        val area: String,
        val industry: String,
        val anyArea: String,
        val anyIndustry: String,
        /** The area and industry lists when nothing matches what was typed. */
        val noFilterMatches: String,
        val clearFilters: String,
        val filtersLocked: String,
        /** Why the other filter is disabled while one is chosen: a mix filters by an area or an industry. */
        val filtersExclusive: String,
        /**
         * The phone filters row (Penpot `Mix Builder / Compact`, design deviation H1) when the plan has no filters: with
         * no filter chosen, and with one left on the draft (named by the argument); the badge beside it.
         */
        val filtersNeedPlan: String,
        val filtersNeedPlanWith: (filter: String) -> String,
        val premium: String,
        /** The phone top bar's menu of the builder's other actions (Share, Re-evaluate). */
        val moreActions: String,
        val weightsLocked: String,
        val median: String,
        val middleHalf: String,
        val typicalRange: String,
        val forYear: String,
        val chartTitle: String,
        val chartDescription: String,
        val projection: String,
        val projectionMore: String,
        val projectionLess: String,
        val dataQualityTitle: String,
        val backfilled: String,
        /** Why the current year is projected, given the year of the latest BLS release. */
        val projected: (latestReleaseYear: Int) -> String,
        val capped: String,
        val missing: String,
        /** An occupation's caption when the stats response has no wages for it, so it is not part of the mix. */
        val occupationNoData: String,
        /** The same, when an area or industry filter is set. */
        val occupationNoDataForFilter: String,
        /**
         * An occupation's caption when the response skipped these actual [years] and they were estimated by a linear
         * regression over the [basedOn] years; with one [basedOn] year the estimate is flat at that year's figures.
         */
        val occupationEstimated: (years: List<Int>, basedOn: List<Int>) -> String,
        val saveMix: String,
        val updateMix: String,
        val share: String,
        val discard: String,
        val breakdown: String,
        val occupationColumn: String,
        val shareColumn: String,
        val medianColumn: String,
        val noStats: String,
        /** A filtered stats request came back without any data. */
        val noDataForFilters: String,
        val tryTitle: String,
        val tryLead: String,
        val loggedOutTitle: String,
        val loggedOutMessage: String,
        val freeTitle: String,
        val freeMessage: String,
        /** The guided tour a logged-out visitor sees until they finish or skip it. */
        val tutorial: MixBuilderTutorial,
        val row: WMOccupationRowStrings,
        val saveDialogTitle: String,
        val saveDialogDescription: String,
        val mixTitle: String,
        val mixTitleHelper: String,
        val updateDialogTitle: String,
        val updateDialogDescription: String,
        val saveAsNew: String,
        val discardDialogTitle: String,
        val discardDialogDescription: String,
        val premiumTitle: String,
        val premiumWeights: String,
        val premiumFilters: String,
        val premiumSave: String,
        val premiumShare: String,
        val premiumAiMix: String,
        val signInTitle: String,
        val signInMessage: String
    )

    data class MixBuilderTutorial(
        /** Where the visitor is in the tour, given the 1-based [step] of [count]. */
        val progress: (step: Int, count: Int) -> String,
        val skip: String,
        val next: String,
        val done: String,
        val addOccupationsTitle: String,
        val addOccupationsMessage: String,
        /** The step's button, which opens the occupation picker. */
        val addOccupationsAction: String,
        val chartDataTitle: String,
        val chartDataMessage: String,
        val chartModeTitle: String,
        val chartModeMessage: String,
        val chartProjectionTitle: String,
        val chartProjectionMessage: String
    )

    data class Picker(
        val title: String,
        val searchPlaceholder: String,
        /** The line under the search field: the query is sent to analytics, so nothing personal belongs in it. */
        val searchNotice: String,
        val selected: String,
        val clear: String,
        val addToMix: String,
        val noResults: String,
        val loadFailed: String,
        /** The add was refused: a mix holds at most this many occupations. */
        /** The notice when a selection would pass the limit (Penpot `… · Browse Occupations · Limit`). */
        val tooManyOccupationsTitle: (limit: Int) -> String,
        val tooManyOccupationsMessage: (attempted: Int) -> String,
        val row: WMTreeRowStrings
    )

    data class SavedMixes(
        val title: String,
        val all: String,
        val fromJobDescriptions: String,
        val searchPlaceholder: String,
        val emptyTitle: String,
        val emptyMessage: String,
        val noMatches: String,
        val open: String,
        val share: String,
        val rename: String,
        val duplicate: String,
        val delete: String,
        val more: String,
        val occupations: String,
        val medianCaption: String,
        val card: WMMixCardStrings,
        val renameTitle: String,
        val deleteTitle: String,
        val deleteDescription: String
    )

    data class JobDescriptions(
        val title: String,
        val uploadFile: String,
        val pasteUrl: String,
        val urlPlaceholder: String,
        val analyze: String,
        val supportedBoards: String,
        /** Under the dropzone and the URL entry: uploads are not checked for personal information (privacy plan step 0). */
        val privacyNote: String,
        val privacyPolicy: String,
        val quota: String,
        val quotaUnlimited: String,
        val uploads: String,
        val emptyTitle: String,
        val emptyMessage: String,
        val openMix: String,
        /** An upload row's button: previews the stored file, which the preview can download. */
        val viewFile: String,
        val filePreview: WMFilePreviewStrings,
        /** The dialog when an upload's file is no longer stored. */
        val fileUnavailableTitle: String,
        val fileUnavailableMessage: String,
        val reevaluate: String,
        /** Why an upload produced no mix (dialog titles), and what the user can do (job-description-intake-plan §3). */
        val notJobDescriptionTitle: String,
        val pageUnavailableTitle: String,
        val maliciousTitle: String,
        val analysisFailedTitle: String,
        /** For a posting URL: print the page to PDF and upload that. */
        val remedyPrintToPdf: String,
        /** For an uploaded file that is not a job description. */
        val remedyUploadTheJobDescription: String,
        val remedyMalicious: String,
        val remedyRetry: String,
        /** Failed uploads are free. */
        val notCounted: String,
        /** A failed upload's button: runs its analysis again on the file already uploaded. */
        val retryUpload: String,
        /** A failed upload once the plan's [quota] uploads are used: Retry is off (plan rule 7). */
        val retryAtLimit: (quota: Int) -> String,
        /** A failed upload's Delete button, its confirmation and its failure (plan rule 8). */
        val deleteUpload: String,
        val deleteUploadTitle: String,
        val deleteUploadMessage: (name: String) -> String,
        val deleteUploadFailedTitle: String,
        val processing: String,
        val overQuotaTitle: String,
        val overQuotaMessage: String,
        val dropzone: WMUploadDropzoneStrings,
        val row: WMUploadRowStrings,
        val limitTitle: String,
        val limitMessage: String,
        val invalidFileTitle: String,
        val unsupportedType: String,
        val tooLarge: String,
        val emptyFile: String,
        val invalidUrlTitle: String,
        val urlBlank: String,
        val urlMalformed: String,
        val urlUnsupported: String,
        val failedTitle: String,
        val failedMessage: String,
        val reevaluateTitle: String,
        val reevaluateDescription: String
    )

    data class Share(
        val title: String,
        val description: String,
        val payRange: String,
        val payRangeHelper: String,
        val low: String,
        val high: String,
        val lowAboveHigh: String,
        val notPositive: String,
        val badgePreview: String,
        val noBadge: String,
        val statsLoading: String,
        val forJobPosting: String,
        val create: String,
        val linkReady: String,
        val linkReadyDescription: String,
        val copy: String,
        val copied: String,
        val openLink: String,
        val previewLoading: String,
        /** The preview image's alt text. */
        val previewImage: String,
        /** The preview image failed or stalled; the owner may regenerate it. */
        val previewFailedTitle: String,
        val previewFailedMessage: String,
        val regeneratePreview: String,
        /** Asking for the preview again failed (the prompt). */
        val regenerateFailedTitle: String,
        val regenerateFailedMessage: String,
        val shareAnother: String,
        val failedTitle: String,
        val failedMessage: String,
        val sessionExpiredTitle: String,
        val sessionExpiredMessage: String,
        val tierName: (ai.wagemix.net.models.WMBadge) -> String,
        val tierExplanation: (ai.wagemix.net.models.WMBadge) -> String
    )

    data class Account(
        val title: String,
        val profile: String,
        val firstName: String,
        val lastName: String,
        val email: String,
        val verified: String,
        val unverified: String,
        val saveProfile: String,
        val password: String,
        val sendResetLink: String,
        val resetLinkSent: String,
        val subscription: String,
        val plan: String,
        val renews: String,
        val endsOn: String,
        val manageBilling: String,
        /** Under a name field past the limit (Penpot `Account / Name Too Long`): the limit, then this name's length. */
        val firstNameTooLong: (limit: Int, length: Int) -> String,
        val lastNameTooLong: (limit: Int, length: Int) -> String,
        /** Under the subscription card's actions (design deviation E2). */
        val billingByStripe: String,
        val noSubscription: String,
        val uploads: String,
        val profileSavedTitle: String,
        val profileSavedMessage: String
    )

    data class Pricing(
        val title: String,
        val subtitle: String,
        val perYear: String,
        val currentPlan: String,
        val choose: String,
        val startFree: String,
        val freeDescription: String,
        val basicDescription: String,
        val proDescription: String,
        val freeFeatures: List<String>,
        val basicFeatures: List<String>,
        val proFeatures: List<String>,
        val popular: String,
        /** The badge on the plan a lapsed account had, in place of [popular]. */
        val yourPlan: String,
        /** The notice above the plans for a lapsed account; the title's argument names the plan it had. */
        val renewNoticeTitle: (plan: String) -> String,
        val renewNoticeMessage: String,
        val compareTitle: String,
        val featureColumn: String,
        /** Rows of the comparison table: feature name, then the Free / Basic / Pro cell text ("" = not included, "✓" = included). */
        val comparison: List<Pair<String, List<String>>>,
        val faqTitle: String,
        val faq: List<Pair<String, String>>,
        val signInTitle: String,
        val signInMessage: String,
        val verifyTitle: String,
        val verifyMessage: String,
        val resend: String,
        val sentTitle: String,
        val sentMessage: String
    )

    data class Payment(
        val title: String,
        val thanks: String,
        val plan: String,
        val billingCycle: String,
        val nextPayment: String,
        val goToMix: String,
        val failedTitle: String,
        val failedMessage: String
    )

    data class Auth(
        /** The password fields' reveal button, while the password is hidden and while it is shown. */
        val showPassword: String,
        val hidePassword: String,
        val loginTitle: String,
        val loginSubtitle: String,
        val registerTitle: String,
        val registerSubtitle: String,
        val forgotTitle: String,
        val forgotSubtitle: String,
        val checkEmailTitle: String,
        val checkEmailMessage: String,
        val resetTitle: String,
        val resetSubtitle: String,
        val resetDoneTitle: String,
        val resetDoneMessage: String,
        val verifiedTitle: String,
        val verifiedMessage: String,
        val verifyingTitle: String,
        val firstName: String,
        val lastName: String,
        val email: String,
        val password: String,
        val confirmPassword: String,
        val forgotPassword: String,
        val noAccount: String,
        val haveAccount: String,
        val backToLogin: String,
        val continueLabel: String,
        val sendLink: String,
        val setPassword: String,
        val brandTitle: String,
        val brandMessage: String,
        /** The auth brand panel (Penpot `Auth / …` brand side). */
        val brandHeadline: String,
        val brandLead: String,
        /** The sample mix in the auth brand panel's preview card. */
        val previewTitle: String,
        val previewBadge: String,
        val createOne: String,
        val rememberedIt: String,
        val resendEmail: String,
        val resetLinkSentMessage: (email: String) -> String,
        val newPassword: String,
        val confirmNewPassword: String,
        /** The password rules under a new password, before anything is typed. */
        val passwordRules: String,
        /** "By creating an account you agree to the " [termsOfUse] [consentJoin] [privacyPolicy] "." */
        val consentPrefix: String,
        val consentJoin: String,
        val termsOfUse: String,
        val privacyPolicy: String,
        val copyright: String,
        val nameBlank: String,
        /** A name or password past the limit: the field, the limit, then this value's length. */
        val tooLong: (field: String, limit: Int, length: Int) -> String,
        val emailBlank: String,
        val emailInvalid: String,
        val passwordBlank: String,
        val passwordTooShort: String,
        val passwordNoDigit: String,
        val passwordNoUppercase: String,
        val passwordNoLowercase: String,
        val passwordMismatch: String,
        val failedTitle: String,
        val failed: (ai.wagemix.net.models.WMAuthFailure) -> String,
        val notVerifiedTitle: String,
        val notVerifiedMessage: String,
        val resend: String
    )

    /** The shared links page: the links the user has shared, each with its preview image. */
    data class SharedLinks(
        val title: String,
        val emptyTitle: String,
        val emptyMessage: String,
        /** "Shared 2026-10-26": the share's date. */
        val sharedOn: (date: String) -> String,
        val jobPosting: String,
        val social: String,
        val copyLink: String,
        val copied: String,
        val openLink: String,
        /** The switch's accessible name, for the link to [title]. */
        val linkSwitchLabel: (title: String) -> String,
        val linkOn: String,
        /** What a visitor sees once the link is off. */
        val linkOff: String,
        /** "12 opens (3 while off)", on Pro. */
        val opens: (count: Long, whileOff: Long) -> String,
        /** "Last opened 2026-10-27 14:05", on Pro. */
        val lastOpened: (dateTime: String) -> String,
        val notOpenedYet: String,
        /** A card whose statistics are stored, on any plan but Pro: Pro shows them. */
        val statsLocked: String,
        /** A card shared before link statistics were added: there are none, on any plan. */
        val statsUntracked: String,
        val activationFailedTitle: String,
        val activationFailedMessage: String,
    )

    data class SharedMix(
        val wageAnalysis: String,
        val badgeIntro: String,
        val payRange: String,
        val occupations: String,
        val aboutTitle: String,
        val aboutText: String,
        val ctaTitle: String,
        val ctaMessage: String,
        val ctaButton: String,
        val invalidTitle: String,
        val invalidMessage: String,
        val notFoundTitle: String,
        val notFoundMessage: String,
        /** The owner turned the link off. */
        val deactivatedTitle: String,
        val deactivatedMessage: String,
        val failedTitle: String,
        val failedMessage: String,
        val sharedBy: String
    )

    /**
     * A lapsed account (paid-feature plan W7): the shell's banner, the plan card, the locked screens, the dialog a
     * refused action opens and the builder's locked chart. Penpot `Wagemix / Renewal Banner` and the `… / Lapsed` boards.
     */
    data class Renewal(
        val bannerTitle: String,
        val bannerMessage: String,
        val renew: String,
        /** The plan card's badge and text, and its shorter button. */
        val planEnded: String,
        val planEndedText: String,
        val planRenew: String,
        /**
         * The same text and the renew buttons (banner, plan card, dialog, pricing) when the plan the account had is
         * known; the argument names it.
         */
        val planEndedTextOf: (plan: String) -> String,
        val renewPlan: (plan: String) -> String,
        /** The sidebar's mix list heading while the mixes are hidden. */
        val sidebarMixesLocked: String,
        val savedMixesTitle: String,
        val savedMixesMessage: String,
        val uploadsTitle: String,
        val uploadsMessage: String,
        val sharedLinksTitle: String,
        val sharedLinksMessage: String,
        val dialogTitle: String,
        val dialogMessage: String,
        /** The builder's card in place of the chart when the API refuses the draft's filter; the argument names it. */
        val chartLockedTitle: (filter: String) -> String,
        val chartLockedMessage: String,
        /** The same card for an account that never paid (a filter left on a draft from a paid session). */
        val chartLockedUpgradeMessage: String,
        val clearFilter: String
    )

    companion object {
        val english: WMWebStrings by lazy { englishWebStrings() }
    }
}

/** [plan]'s name as the site shows it. */
internal fun WMWebStrings.planName(plan: WMSubscriptionType): String = when (plan) {
    WMSubscriptionType.None -> common.free
    WMSubscriptionType.Basic -> common.basic
    WMSubscriptionType.Pro -> common.pro
}
